import { apiErrorText } from '@/api/errors';
import { ChipInput } from '@/components/ChipInput';
import MonthCalendarPopover from '@/components/MonthCalendarPopover';
import { useErrorT, useT } from '@/i18n';
import { useRestaurantTimezone } from '@/stores/restaurant.store';
import type { Exception, Shift } from '@restaurant/api-contracts';
import {
  Block,
  BlockFooter,
  BlockTitle,
  Button,
  List,
  ListButton,
  ListInput,
  ListItem,
  Navbar,
  NavLeft,
  NavRight,
  NavTitle,
  Link as F7Link,
  Page,
  Preloader,
  Segmented,
  f7,
} from 'framework7-react';
import { useEffect, useRef, useState } from 'react';
import {
  useCreateException,
  useDeleteException,
  useExceptions,
  useUpdateException,
} from '../hooks/useExceptions';

/** Compact label for a date pill - matches the reservation date chip format. */
function formatDateChip(value: string): string {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/** Date-pill trigger styling - mirrors the reservation create/edit date chip. */
const DATE_PILL_STYLE = {
  background: 'rgba(120,120,128,0.12)',
  borderRadius: 6,
  padding: '4px 10px',
  border: 'none',
  cursor: 'pointer',
  fontSize: 17,
  lineHeight: '24px',
  color: 'var(--f7-theme-color)',
  whiteSpace: 'nowrap',
} as const;

type ExceptionType = Exception['type'];
type ServiceType = Exception['services'][number];

// The services a holiday/exception can apply to, paired with their `openingHours`
// i18n key (labels resolved against the current locale inside the component).
// Selecting all of them = "all services" (there is no separate all/null value).
const SERVICE_OPTIONS: {
  id: ServiceType;
  key: 'openingTime' | 'pickup' | 'delivery' | 'reservation';
}[] = [
  { id: 'BASE', key: 'openingTime' },
  { id: 'PICKUP', key: 'pickup' },
  { id: 'DELIVERY', key: 'delivery' },
  { id: 'RESERVATION', key: 'reservation' },
];

const ALL_SERVICES = SERVICE_OPTIONS.map((s) => s.id);

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

function todayInTz(tz: string): string {
  // en-CA renders YYYY-MM-DD; timeZone keeps "today" anchored to the venue.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Add / edit a holiday or closure. This used to be a PushSheet, but the form is
 * tall (scope, type, dates, multi-shift special hours) and the sheet layout
 * broke - so it's a classic router page now (Rule: a multi-section flow is a
 * pushed page, not a modal sheet). Reached from ExceptionsPage.
 */
export default function ExceptionFormPage({
  f7route,
  f7router,
}: {
  f7route: { params: { exceptionId?: string } };
  f7router: { back: () => void };
}) {
  const { exceptionId } = f7route.params;
  const editing = !!exceptionId;
  const t = useT();
  const errorMessages = useErrorT();
  // Concise summary for the row's right side (it can't wrap): all four →
  // "All services", one → its name, otherwise a count so it never overflows.
  const servicesLabel = (svcs: ServiceType[]) => {
    if (svcs.length === SERVICE_OPTIONS.length) return t.openingHours.allServices;
    if (svcs.length === 1) {
      const only = SERVICE_OPTIONS.find((o) => o.id === svcs[0]);
      if (only) return t.openingHours[only.key];
    }
    return t.openingHours.servicesSelected(svcs.length);
  };

  const tz = useRestaurantTimezone();
  const { data: exceptions } = useExceptions();
  const exception = editing ? exceptions?.find((e) => e.id === exceptionId) : undefined;

  const create = useCreateException();
  const update = useUpdateException();
  const del = useDeleteException();

  const [services, setServices] = useState<ServiceType[]>(ALL_SERVICES);
  const [type, setType] = useState<ExceptionType>('CLOSED');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [seeded, setSeeded] = useState(false);
  const startCalRef = useRef<HTMLButtonElement>(null);
  const endCalRef = useRef<HTMLButtonElement>(null);
  const [startCalOpen, setStartCalOpen] = useState(false);
  const [endCalOpen, setEndCalOpen] = useState(false);

  // Full list of chosen services, spelled out as a caption under the group so
  // the operator sees exactly which ones without opening the picker - only when
  // the row shows an opaque count (a partial selection of 2–3).
  const servicesFullList = SERVICE_OPTIONS.filter((o) => services.includes(o.id))
    .map((o) => t.openingHours[o.key])
    .join(', ');
  const showServicesFooter = services.length > 1 && services.length < SERVICE_OPTIONS.length;

  // Seed the draft once: new → today + defaults; edit → the loaded exception.
  // Editing waits for the list query to resolve the record before seeding.
  useEffect(() => {
    if (seeded) return;
    if (editing && !exception) return;
    const today = todayInTz(tz);
    setServices(exception?.services ?? ALL_SERVICES);
    setType(exception?.type ?? 'CLOSED');
    setStartDate(exception?.startDate ?? today);
    setEndDate(exception?.endDate ?? today);
    setReason(exception?.reason ?? '');
    setShifts(exception?.shifts?.map((s) => ({ ...s })) ?? [{ open: '09:00', close: '17:00' }]);
    setSeeded(true);
  }, [seeded, editing, exception, tz]);

  // The form was pushed on top of the Holidays list within its own view, so Back
  // just pops that view's stack.
  const back = () => f7router.back();

  function setShift(i: number, key: keyof Shift, value: string) {
    setShifts((prev) => prev.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)));
  }
  const addShift = () => setShifts((prev) => [...prev, { open: '17:00', close: '22:00' }]);
  const removeShift = (i: number) => setShifts((prev) => prev.filter((_, idx) => idx !== i));

  function submit() {
    if (!startDate || !endDate) return toast(t.openingHours.pickStartEndDate);
    if (endDate < startDate) return toast(t.openingHours.endDateBeforeStart);
    if (services.length === 0) return toast(t.openingHours.pickAtLeastOneService);
    if (type === 'MODIFIED') {
      if (shifts.length === 0) return toast(t.openingHours.addSpecialHours);
      for (const s of shifts) {
        if (!s.open || !s.close || s.open >= s.close) {
          return toast(t.openingHours.checkSpecialHours);
        }
      }
    }

    const trimmedReason = reason.trim();
    const resolvedShifts = type === 'MODIFIED' ? shifts : null;
    const onSuccess = () => {
      toast(editing ? t.openingHours.closureUpdated : t.openingHours.closureAdded);
      back();
    };
    const onError = (e: unknown) =>
      toast(apiErrorText(e, errorMessages, t.openingHours.couldNotSave));

    if (editing && exceptionId) {
      update.mutate(
        {
          exceptionId,
          data: {
            services,
            type,
            startDate,
            endDate,
            shifts: resolvedShifts,
            reason: trimmedReason || null,
          },
        },
        { onSuccess, onError },
      );
    } else {
      create.mutate(
        {
          services,
          type,
          startDate,
          endDate,
          shifts: resolvedShifts,
          reason: trimmedReason || undefined,
        },
        { onSuccess, onError },
      );
    }
  }

  function remove() {
    if (!exceptionId) return;
    f7.dialog.confirm(
      t.openingHours.removeThisClosureQuestion,
      t.openingHours.removeClosureConfirm,
      () => {
        del.mutate(exceptionId, {
          onSuccess: () => {
            toast(t.openingHours.closureRemoved);
            back();
          },
          onError: (e) => toast(apiErrorText(e, errorMessages, t.openingHours.couldNotRemove)),
        });
      },
    );
  }

  const saving = create.isPending || update.isPending;
  // Editing an id that isn't in the list (deep link before load / not found).
  const loading = editing && !exception && !seeded;

  return (
    <Page name="exception-form">
      <Navbar>
        <NavLeft>
          <F7Link onClick={back} iconF7="chevron_left" />
        </NavLeft>
        <NavTitle>{editing ? t.openingHours.editClosure : t.openingHours.addClosure}</NavTitle>
        <NavRight>
          <F7Link onClick={saving || loading ? undefined : submit}>{t.saveShort}</F7Link>
        </NavRight>
      </Navbar>

      {loading ? (
        <Block className="text-align-center" style={{ paddingTop: 48 }}>
          <Preloader />
        </Block>
      ) : (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={t.openingHours.reason}
              type="text"
              placeholder={t.openingHours.reasonPlaceholder}
              value={reason}
              outline={f7.theme !== 'ios'}
              floatingLabel={f7.theme !== 'ios'}
              onInput={(e) => setReason((e.target as HTMLInputElement).value)}
            />
          </List>

          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem
              title={t.openingHours.appliesToLabel}
              after={servicesLabel(services)}
              smartSelect
              smartSelectParams={{
                openIn: 'page',
                // Multi-select: keep the page open so several services can be
                // toggled before going Back (no pop on each tap).
                closeOnSelect: false,
                pageTitle: t.openingHours.appliesToLabel,
                // We render our own concise `after` summary (count / "All
                // services"); F7's auto value text is the long joined list
                // that overflows the row, so turn it off.
                setValueText: false,
                // F7's generated list is edge-to-edge; this class scopes the
                // inset/grouped-card look to match the rest of the app.
                cssClass: 'ss-inset-page',
              }}
            >
              <select
                multiple
                value={services}
                onChange={(e) => {
                  const picked = Array.from((e.target as HTMLSelectElement).selectedOptions).map(
                    (o) => o.value,
                  );
                  setServices(
                    SERVICE_OPTIONS.filter((s) => picked.includes(s.id)).map((s) => s.id),
                  );
                }}
              >
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {t.openingHours[s.key]}
                  </option>
                ))}
              </select>
            </ListItem>
          </List>
          {showServicesFooter && <BlockFooter>{servicesFullList}</BlockFooter>}

          <BlockTitle>{t.openingHours.whatHappens}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{t.openingHours.whatHappensHint}</BlockFooter>
          <Block>
            <Segmented strong>
              <Button active={type === 'CLOSED'} onClick={() => setType('CLOSED')}>
                {t.openingHours.holiday}
              </Button>
              <Button active={type === 'MODIFIED'} onClick={() => setType('MODIFIED')}>
                {t.openingHours.exception}
              </Button>
            </Segmented>
          </Block>

          <BlockTitle>{t.openingHours.dates}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{t.openingHours.singleDayHint}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title={t.openingHours.from}>
              <button
                slot="after"
                ref={startCalRef}
                type="button"
                className="no-ripple"
                aria-label={t.openingHours.startDateAriaLabel}
                onClick={() => setStartCalOpen(true)}
                style={DATE_PILL_STYLE}
              >
                {formatDateChip(startDate) || '-'}
              </button>
            </ListItem>
            <ListItem title={t.openingHours.to}>
              <button
                slot="after"
                ref={endCalRef}
                type="button"
                className="no-ripple"
                aria-label={t.openingHours.endDateAriaLabel}
                onClick={() => setEndCalOpen(true)}
                style={DATE_PILL_STYLE}
              >
                {formatDateChip(endDate) || '-'}
              </button>
            </ListItem>
          </List>

          <MonthCalendarPopover
            open={startCalOpen}
            onClose={() => setStartCalOpen(false)}
            anchorEl={startCalRef.current}
            selectedDate={startDate}
            onSelect={(v) => {
              setStartDate(v);
              if (endDate < v) setEndDate(v);
              setStartCalOpen(false);
            }}
          />
          <MonthCalendarPopover
            open={endCalOpen}
            onClose={() => setEndCalOpen(false)}
            anchorEl={endCalRef.current}
            selectedDate={endDate}
            minDate={startDate}
            onSelect={(v) => {
              setEndDate(v);
              setEndCalOpen(false);
            }}
          />

          {type === 'MODIFIED' && (
            <>
              <BlockTitle>{t.openingHours.specialHours}</BlockTitle>
              <BlockFooter style={{ marginTop: -6 }}>
                {t.openingHours.hoursReplaceWeeklyNote}
              </BlockFooter>
              <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                {shifts.map((s, i) => (
                  <ListItem
                    key={i}
                    title={
                      shifts.length > 1
                        ? t.openingHours.shiftLabel(i + 1)
                        : t.openingHours.openLabel
                    }
                  >
                    <div slot="after" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ChipInput
                        type="time"
                        value={s.open}
                        onChange={(v) => setShift(i, 'open', v)}
                        aria-label={t.openingHours.openTimeAria(t.openingHours.shiftLabel(i + 1))}
                      />
                      <span style={{ color: 'var(--f7-list-item-subtitle-text-color)' }}>–</span>
                      <ChipInput
                        type="time"
                        value={s.close}
                        onChange={(v) => setShift(i, 'close', v)}
                        aria-label={t.openingHours.closeTimeAria(t.openingHours.shiftLabel(i + 1))}
                      />
                      <i
                        className="f7-icons"
                        style={{ fontSize: 20, color: '#ff3b30', cursor: 'pointer' }}
                        onClick={() => removeShift(i)}
                        aria-label={t.openingHours.removeHoursAriaLabel}
                      >
                        minus_circle
                      </i>
                    </div>
                  </ListItem>
                ))}
                <ListItem title={t.openingHours.addHours} link="#" onClick={addShift}>
                  <i slot="media" className="f7-icons" style={{ color: 'var(--f7-theme-color)' }}>
                    plus_circle
                  </i>
                </ListItem>
              </List>
            </>
          )}

          {editing && (
            <List strongIos strongMd insetIos insetMd>
              <ListButton color="red" onClick={remove}>
                {t.openingHours.deleteThisClosure}
              </ListButton>
            </List>
          )}
        </>
      )}
    </Page>
  );
}
